import { LabReportTemplate } from './types';
import { CURRENT_SCHEMA_VERSION, DocumentSection } from '../../core/ast/document.types';
import { createDefaultPageSettings, createDefaultMetadata } from '../../core/ast/factory';
import { labReportCoverTemplate } from '../cover/labReportCover';
import { getDefaultCoverValues } from '../cover/templateUtils';

export const zenerDiodeLabReportTemplate: LabReportTemplate = {
  id: 'tpl-lab-zener-diode',
  name: 'Study of Zener Diode & Voltage Regulation (CSE-251 Expt 03)',
  category: 'electronics',
  description: 'Complete lab report blueprint with reverse breakdown modeling, line regulation, load regulation (%) calculations, multi-table experimental datasheets, and circuit diagrams.',
  thumbnailBadge: 'Zener Diode',
  version: 2,
  sectionsBlueprint: [
    { title: 'Cover Page', type: 'cover', defaultContent: '' },
    { title: 'Title & Objectives', type: 'title', defaultContent: 'Title: Study of Zener Diode\nObjectives:\n1. To measure the I-V characteristics of a Zener diode.\n2. To evaluate voltage stabilization under varying load and supply voltage.' },
    { title: 'Theory (Summary) & Equipment', type: 'theory', defaultContent: 'A Zener diode is designed to operate stably in the reverse breakdown region without damage.' },
    { title: 'Circuit Diagram', type: 'custom', defaultContent: 'Circuit diagrams for no load, variable load, and variable supply voltage configurations.' },
    { title: 'Pre-Lab Report Calculations', type: 'custom', defaultContent: 'Theoretical I-V table, condition for conduction, minimum load resistance, and input voltage regulation limits.' },
    { title: 'Experimental Datasheet (Tables 1, 2, 3)', type: 'data', defaultContent: 'Measurement datasets for I-V characteristics, load regulation, and line regulation.' },
    { title: 'Post-Lab Questions, Calculations & Curves', type: 'custom', defaultContent: 'Percentage load and line regulation calculations and breakdown voltage evaluation.' },
    { title: 'Discussion', type: 'discussion', defaultContent: 'This experiment aimed to explore the behavior of a Zener diode, specifically its I-V characteristics and voltage regulation ability.' },
  ],
  compileToDocument: (customTitle?: string, pageSettingsOverrides?) => {
    const now = new Date().toISOString();

    const coverVals = getDefaultCoverValues({
      documentTypeLabel: 'Lab Report',
      title: 'Lab Report',
      experimentNumber: '03',
      experimentName: 'Study of Zener Diode',
      groupNumber: '',
      courseCode: 'CSE251',
      courseTitle: 'Electronic Circuits',
      sectionOrGroup: '',
      semester: '',
      studentName: '',
      studentId: '',
      facultyName: '',
      facultyDesignation: '',
      facultyDepartment: '',
      dateOfPerformance: '',
      submissionDate: '',
      submissionDisplayMode: 'both',
    });
    const coverDoc = labReportCoverTemplate.compileToDocument(coverVals, pageSettingsOverrides);
    const coverSection = coverDoc.sections[0];

    const sections: DocumentSection[] = [
      coverSection,
      // Section 1: Title, Objectives, Theory & Equipment
      {
        id: `sec-zener-title-${Date.now().toString(36)}`,
        title: 'Title, Objectives & Theory',
        type: 'title',
        enabled: true,
        collapsed: false,
        settings: { pageBreakBefore: true },
        blocks: [
          {
            id: `blk-z-t1-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: 'Title: Study of Zener Diode\n\nObjectives:\n1. To measure the I-V (Current vs. Voltage) characteristics of a Zener diode.\n2. To check how well the Zener diode keeps the voltage steady when the load or supply voltage changes.\n\nTheory (Summary):\nA Zener diode is a special type of diode made to work in the reverse breakdown region, where normal diodes can get damaged. The Zener diode is used in voltage regulator circuits because it can keep the voltage almost constant even if the input voltage or the load changes.\n\nThere are three main regions of Zener diode operation:\n1. Forward region: Works like a normal diode.\n2. Leakage region: Small current flows before breakdown.\n3. Breakdown region: Voltage stays steady, current increases.\n\nEquipment:\n1. Zener diode (6.2V)\n2. Resistors (220 Ω, 470 Ω, 1 kΩ)\n3. POT 10 kΩ\n4. DC Power supply\n5. Breadboard\n6. Multimeter & Ammeters',
              formatting: { fontSizePt: 10.5, lineHeightMultiplier: 1.45 },
            },
          },
        ],
      },
      // Section 2: Circuit Diagrams
      {
        id: `sec-zener-circuit-${Date.now().toString(36)}`,
        title: 'Circuit Diagrams',
        type: 'custom',
        enabled: true,
        collapsed: false,
        blocks: [
          {
            id: `blk-z-circ-h-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: 'Circuit Diagrams:\n• Figure-1: Circuit with no load (I-V curve testing)\n• Figure-2: Circuit with variable load (Load regulation testing)\n• Figure-3: Circuit with variable supply voltage (Line regulation testing)',
              formatting: { bold: true, fontSizePt: 10.5, color: '#1f2937' },
            },
          },
        ],
      },
      // Section 3: Pre-Lab Calculations & Derivations
      {
        id: `sec-zener-prelab-${Date.now().toString(36)}`,
        title: 'Pre-Lab Calculations & Regulation Limits',
        type: 'custom',
        enabled: true,
        collapsed: false,
        settings: { pageBreakBefore: true },
        blocks: [
          {
            id: `blk-z-pl-q1-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: 'Pre-Lab Report Analysis:\n\n1. Regulation Condition with Load RL = 220 Ω:\nFor Vi = 10 V, Rs = 465 Ω, Vz = 7.2 V:\n• Total supply current: I = (Vi - Vz) / Rs = (10 - 7.2) / 465 = 6.02 mA\n• Required load current: IL = Vz / RL = 7.2 / 220 = 32.73 mA\n• Resulting Zener current: Iz = I - IL = 6.02 mA - 32.73 mA = -26.71 mA (Negative current is impossible)\nConclusion: The Zener diode cannot regulate with RL = 220 Ω because supply current is insufficient to drive the load.',
              formatting: { fontSizePt: 10.5, lineHeightMultiplier: 1.45 },
            },
          },
          {
            id: `blk-z-pl-f1-${Date.now().toString(36)}`,
            type: 'formula',
            createdAt: now,
            data: {
              latex: 'V_{i\\text{min}} = V_Z + (I_R \\times R_s) = 7.2\\text{ V} + (7.2\\text{ mA} \\times 465\\ \\Omega) = 10.55\\text{ V}',
              displayMode: true,
              equationNumber: '(Eq. 1)',
              caption: 'Minimum Input Voltage for Regulation',
            },
          },
          {
            id: `blk-z-pl-f2-${Date.now().toString(36)}`,
            type: 'formula',
            createdAt: now,
            data: {
              latex: 'V_{i\\text{max}} = V_Z + [(I_{Z\\text{max}} + I_L) \\times R_s] = 7.2\\text{ V} + (14.4\\text{ mA} \\times 465\\ \\Omega) = 13.90\\text{ V}',
              displayMode: true,
              equationNumber: '(Eq. 2)',
              caption: 'Maximum Input Voltage for Regulation (IZ ≤ 7.2 mA)',
            },
          },
        ],
      },
      // Section 4: Experimental Datasheets
      {
        id: `sec-zener-data-${Date.now().toString(36)}`,
        title: 'Experimental Datasheet (Tables 1, 2, 3)',
        type: 'data',
        enabled: true,
        collapsed: false,
        settings: { pageBreakBefore: true },
        blocks: [
          {
            id: `blk-z-t1-h-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: 'Table 1: Data for I-V Characteristics',
              formatting: { bold: true, fontSizePt: 11, color: '#111827' },
            },
          },
          {
            id: `blk-z-tbl1-${Date.now().toString(36)}`,
            type: 'table',
            createdAt: now,
            data: {
              rows: 8,
              cols: 4,
              hasHeaderRow: true,
              hasHeaderCol: false,
              borderStyle: 'academic',
              caption: 'Table 1: Zener Reverse I-V Measurements',
              cells: [
                [
                  { id: 'c00', content: 'Vi (Volt)', isHeader: true, bold: true, alignment: 'center' },
                  { id: 'c01', content: 'VR (Volt)', isHeader: true, bold: true, alignment: 'center' },
                  { id: 'c02', content: 'VZ (Volt)', isHeader: true, bold: true, alignment: 'center' },
                  { id: 'c03', content: 'IZ = VR / R (mA)', isHeader: true, bold: true, alignment: 'center' },
                ],
                [
                  { id: 'c10', content: '1.0', alignment: 'center' },
                  { id: 'c11', content: '0.0', alignment: 'center' },
                  { id: 'c12', content: '0.9', alignment: 'center' },
                  { id: 'c13', content: '0.00', alignment: 'center' },
                ],
                [
                  { id: 'c20', content: '3.0', alignment: 'center' },
                  { id: 'c21', content: '0.1', alignment: 'center' },
                  { id: 'c22', content: '2.7', alignment: 'center' },
                  { id: 'c23', content: '0.215', alignment: 'center' },
                ],
                [
                  { id: 'c30', content: '5.0', alignment: 'center' },
                  { id: 'c31', content: '1.3', alignment: 'center' },
                  { id: 'c32', content: '3.6', alignment: 'center' },
                  { id: 'c33', content: '2.80', alignment: 'center' },
                ],
                [
                  { id: 'c40', content: '7.0', alignment: 'center' },
                  { id: 'c41', content: '2.9', alignment: 'center' },
                  { id: 'c42', content: '3.9', alignment: 'center' },
                  { id: 'c43', content: '6.24', alignment: 'center' },
                ],
                [
                  { id: 'c50', content: '8.0', alignment: 'center' },
                  { id: 'c51', content: '3.8', alignment: 'center' },
                  { id: 'c52', content: '4.0', alignment: 'center' },
                  { id: 'c53', content: '8.17', alignment: 'center' },
                ],
                [
                  { id: 'c60', content: '9.0', alignment: 'center' },
                  { id: 'c61', content: '4.7', alignment: 'center' },
                  { id: 'c62', content: '4.1', alignment: 'center' },
                  { id: 'c63', content: '10.11', alignment: 'center' },
                ],
                [
                  { id: 'c70', content: '10.0', alignment: 'center' },
                  { id: 'c71', content: '5.6', alignment: 'center' },
                  { id: 'c72', content: '4.1', alignment: 'center' },
                  { id: 'c73', content: '12.04', alignment: 'center' },
                ],
              ],
            },
          },
          {
            id: `blk-z-t2-h-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: '\nTable 2: Data for Regulation Due to Load Variation (Vi = 10 V)',
              formatting: { bold: true, fontSizePt: 11, color: '#111827' },
            },
          },
          {
            id: `blk-z-tbl2-${Date.now().toString(36)}`,
            type: 'table',
            createdAt: now,
            data: {
              rows: 5,
              cols: 3,
              hasHeaderRow: true,
              hasHeaderCol: false,
              borderStyle: 'academic',
              caption: 'Table 2: Load Variation Regulation',
              cells: [
                [
                  { id: 'c00', content: 'Vi (Volt)', isHeader: true, bold: true, alignment: 'center' },
                  { id: 'c01', content: 'VL (Volt)', isHeader: true, bold: true, alignment: 'center' },
                  { id: 'c02', content: 'IL (mA) & Load Resistance', isHeader: true, bold: true, alignment: 'center' },
                ],
                [
                  { id: 'c10', content: '10 V', alignment: 'center' },
                  { id: 'c11', content: '3.6 V', alignment: 'center' },
                  { id: 'c12', content: '0.40 mA (10.4 kΩ)', alignment: 'center' },
                ],
                [
                  { id: 'c20', content: '10 V', alignment: 'center' },
                  { id: 'c21', content: '3.6 V', alignment: 'center' },
                  { id: 'c22', content: '0.20 mA (9.08 kΩ)', alignment: 'center' },
                ],
                [
                  { id: 'c30', content: '10 V', alignment: 'center' },
                  { id: 'c31', content: '3.6 V', alignment: 'center' },
                  { id: 'c32', content: '0.17 mA (6.03 kΩ)', alignment: 'center' },
                ],
                [
                  { id: 'c40', content: '10 V', alignment: 'center' },
                  { id: 'c41', content: '3.6 V', alignment: 'center' },
                  { id: 'c42', content: '0.09 mA (1.07 kΩ)', alignment: 'center' },
                ],
              ],
            },
          },
        ],
      },
      // Section 5: Post-Lab Calculations & Regulation Formulations
      {
        id: `sec-zener-postlab-${Date.now().toString(36)}`,
        title: 'Post-Lab Questions & Solutions',
        type: 'custom',
        enabled: true,
        collapsed: false,
        settings: { pageBreakBefore: true },
        blocks: [
          {
            id: `blk-z-pl-h1-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: 'Post-Lab Calculations:\n\n1. Breakdown Voltage Determination:\nFrom Table 1, at Vz = 0.9 V to 3.9 V, the current Iz increases slowly. When Vz reaches 4.0 V to 4.1 V, current suddenly jumps from 8.17 mA to 12.04 mA while voltage remains clamped at 4.1 V, showing sharp Zener breakdown.\n\n2. Calculation of Load Regulation (%):',
              formatting: { fontSizePt: 10.5, lineHeightMultiplier: 1.45 },
            },
          },
          {
            id: `blk-z-pl-f3-${Date.now().toString(36)}`,
            type: 'formula',
            createdAt: now,
            data: {
              latex: '\\text{Load Regulation (\\%)} = \\frac{V_{\\text{NL}} - V_{\\text{FL}}}{V_{\\text{FL}}} \\times 100\\% = \\frac{3.6\\text{ V} - 3.6\\text{ V}}{3.6\\text{ V}} \\times 100\\% = 0.0\\%',
              displayMode: true,
              equationNumber: '(Eq. 3)',
              caption: 'Percentage Load Regulation',
            },
          },
          {
            id: `blk-z-pl-h2-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: '3. Calculation of Line Regulation (%):\nFor ΔVi = 10 V - 1 V = 9 V, ΔVL = 4.05 V - 0.63 V = 3.42 V:',
              formatting: { fontSizePt: 10.5, lineHeightMultiplier: 1.45 },
            },
          },
          {
            id: `blk-z-pl-f4-${Date.now().toString(36)}`,
            type: 'formula',
            createdAt: now,
            data: {
              latex: '\\text{Line Regulation} = \\frac{\\Delta V_L}{\\Delta V_i} \\times 100\\% = \\frac{3.42\\text{ V}}{9.0\\text{ V}} \\times 100\\% = 38.0\\%',
              displayMode: true,
              equationNumber: '(Eq. 4)',
              caption: 'Percentage Line Regulation',
            },
          },
        ],
      },
      // Section 6: Discussion
      {
        id: `sec-zener-disc-${Date.now().toString(36)}`,
        title: 'Discussion',
        type: 'discussion',
        enabled: true,
        collapsed: false,
        settings: { pageBreakBefore: true },
        blocks: [
          {
            id: `blk-z-disc-body-${Date.now().toString(36)}`,
            type: 'text',
            createdAt: now,
            data: {
              text: 'Discussion:\n\nThis experiment successfully examined the electrical behavior of a Zener diode, focusing on its reverse I-V characteristics and voltage regulation capabilities.\n\nFrom the I-V characteristic data, the Zener diode entered breakdown at approximately 4.1 V, maintaining a virtually constant clamp voltage as current surged. Under load variation (Table 2), the output voltage VL remained locked at 3.6 V across varying load currents, achieving ideal load regulation of 0%.\n\nFor line regulation, output voltage changed by 38% across a wide supply span (1 V to 10 V), stabilizing once input exceeded the breakdown threshold. In conclusion, the Zener diode demonstrated excellent load voltage stabilization and verified theoretical reverse breakdown principles.',
              formatting: { fontSizePt: 10.5, lineHeightMultiplier: 1.5 },
            },
          },
        ],
      },
    ];

    return {
      schemaVersion: CURRENT_SCHEMA_VERSION,
      metadata: createDefaultMetadata(customTitle || 'CSE-251 Lab Report 03: Study of Zener Diode', 'lab_report'),
      pageSettings: { ...createDefaultPageSettings(), ...pageSettingsOverrides },
      sections,
    };
  },
};
